import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { useStartBotCRUDActions } from '@mm/state/actions/StartBotCrudActions';
import FBPostModel from '@mm/state/model/FBPostModel';
import { LinkTypes, PromoterModel } from '@mm/state/model/PromoterModel';
import {
  activeFacebookPagePlanInfoSelector,
  activeFacebookPageSelector,
} from '@mm/state/selectors/facebookPageSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import { useCatchAllConfirmModal } from '../commentGuards/use-catch-all-confirm-modal';
import { useStartBotDetails } from './use-startbot-details';

const DEFAULT_CG_MESSAGE =
  'Hey, thanks for leaving a comment! Can I message you here later?';

const useCreateStartBotPromoterBase = (promoterKind: LinkTypes) => {
  const startBotDetails = useStartBotDetails(promoterKind);
  const startBotCRUDActions = useStartBotCRUDActions();
  const {
    history,
    match: { params },
  } = useRouter<{ chatbotId: string }>();

  const chatbotIdFromSelector = useSelector(activeChatbotIdSelector);
  const chatbotId = Number(params.chatbotId) || chatbotIdFromSelector;

  return async (promoterCreateParams?: any) => {
    const response = await startBotCRUDActions.create(
      chatbotId,
      promoterKind,
      promoterCreateParams,
    );

    if (response && response.success && response.startBot) {
      history.push(
        startBotDetails.routes.edit.getLink({
          chatbotId,
          dialogueId: response.startBot.pageId,
          promoterId: response.startBot.promoterId,
        }),
      );
    }
  };
};

export const useCreateCommentGuard = (
  createStartBot?: (promoterCreateParams?: Partial<PromoterModel>) => void,
) => {
  const promoters = useSelector((state: StoreState) => state.promoter.entities);
  const freeOrSpecialPricePlan = useSelector(
    (state: StoreState) =>
      activeFacebookPagePlanInfoSelector(state).info.isFreeOrSpecialPlan,
  );
  const openCatchAllConfirmModal = useCatchAllConfirmModal();
  const chatbotId = useSelector(activeChatbotIdSelector);

  const createCommentGuardStarBotPromoter = (
    promoterCreateParams?: Partial<PromoterModel>,
  ) => {
    const valid = validatePromoter(promoterCreateParams);

    const onSuccess = () => createStartBot?.(promoterCreateParams);

    if (valid) {
      onSuccess();
      return;
    }

    openCatchAllConfirmModal(onSuccess);
  };

  const validatePromoter = (promoterParams?: Partial<PromoterModel>) => {
    const anyCatchAll = promoters.some(
      guard =>
        // only for case when front-end creates catch all guard without
        // keywords  (BE has keywordsEnabled defaults === true)
        guard &&
        guard.botId === chatbotId &&
        guard.active &&
        !guard.keywordsEnabled &&
        guard.postId === 'ALL' &&
        !promoterParams?.keywordsEnabled &&
        promoterParams?.active &&
        promoterParams?.postId === 'ALL',
    );

    return !(anyCatchAll && freeOrSpecialPricePlan);
  };

  return { createCommentGuardStarBotPromoter, validatePromoter };
};

export const useCreateStartBotPromoterByType = (promoterKind: LinkTypes) => {
  const createStartBotPromoterBase =
    useCreateStartBotPromoterBase(promoterKind);
  const { createCommentGuardStarBotPromoter } = useCreateCommentGuard(
    createStartBotPromoterBase,
  );
  const freeOrSpecialPricePlan = useSelector(
    (state: StoreState) =>
      activeFacebookPagePlanInfoSelector(state).info.isFreeOrSpecialPlan,
  );
  const activeFacebookPageId = useSelector(activeFacebookPageSelector)?.id;
  const connectedPageCRUDActions = useConnectedPageCRUDActions();

  switch (promoterKind) {
    case 'fb_comment_guard':
      return (
        message: string = DEFAULT_CG_MESSAGE,
        postId: string = 'id',
        postInfo: FBPostModel = {},
      ) => {
        return createCommentGuardStarBotPromoter({
          postId: freeOrSpecialPricePlan ? 'ALL' : postId,
          message,
          postInfo: freeOrSpecialPricePlan ? {} : postInfo,
          keywordsEnabled: true,
        });
      };
    case 'customer_chat':
      return async () => {
        await createStartBotPromoterBase();
        if (activeFacebookPageId) {
          setTimeout(
            () => connectedPageCRUDActions.retrievePage(activeFacebookPageId),
            1500,
          );
        }
      };
    case 'instagram_comment_guard':
    default:
      return () => createStartBotPromoterBase();
  }
};
